/**
 * section-progress-input.ts — building the input for `computeSectionProgress`, ONCE (s156, F / D-156).
 *
 * Sub-phase F puts the essentials on the same section math the planner's green checks use. Essentials
 * are computed on the SERVER (the advisor portal and the executor task routes consume them and must
 * never see field values); the green checks are computed on the CLIENT. Both must reach the same answer
 * for the same estate, or the dashboard and the essentials card disagree in front of the planner.
 *
 * Tonight's D-156 bug is the argument for this module existing: the two sides each resolved row
 * visibility from what they happened to have, and silently disagreed. So everything derivable from
 * (section config + rows + fields) is decided HERE, and each side supplies only what it alone can know:
 *
 *   • client → Cat A via the fail-closed `isCategoryAField`; showIf visibility via `evaluateShowIf`
 *   • server → Cat A from the DB flag; showIf not evaluated (list sections don't use it)
 *
 * Ruling (Reade, s156): one computation in shared/, callable from both sides. Forking is still
 * forbidden — the rule's intent (never two implementations) is unchanged; only its wording moved on.
 * A parity test drives both adapters over identical fixtures and asserts identical results.
 */

import { EXPANDABLE_LAYOUT_CONFIG } from "./constants/section-layout-config";
import { isLegacyBlankRow, isRowHidden } from "./lib/row-visibility";
import { type ShowIfCondition } from "./gate-eval";
import { hiddenFieldKeysOnRow } from "./lib/row-gates";
import type { SectionProgressInput, SectionProgressField, SectionProgressEntry } from "./section-progress";
import { BENEFICIARIES_SLUG } from "./essentials-spec";
import { isChildBeneficiaryEntries } from "./beneficiary-child";

/** A row as either side can produce it: the raw cells, before visibility is decided. */
export interface RawProgressRow {
  rowId: string;
  entries: Array<{
    /** the DB fieldId — needed to evaluate per-row showIf, which targets a SIBLING by id. */
    fieldId?: string;
    fieldKey: string;
    value: string | null;
    isSeeded: boolean;
    isHidden?: boolean | null;
    declined?: boolean;
  }>;
}

/** A field's gate, as authored on `generic_fields.conditions`, keyed by fieldKey. Supplied by the
 *  caller so the builder can resolve showIf PER ROW (s156 Part B). Absent → the field is ungated. */
export type FieldGates = Record<string, { fieldId: string; conditions: ShowIfCondition | null } | undefined>;

export interface BuildProgressInputArgs {
  sectionSlug: string;
  layoutType: SectionProgressInput["layoutType"];
  /** Does the spec declare an EMPTY STARTER row? REQUIRED (E-fix): it decides whether an all-blank
   *  seeded row is a starter the planner SEES or an artifact the render path hides. Resolved by the
   *  caller from its own defaultRows source — client via completionSectionOf, server from the DB. */
  declaresEmptyStarter: boolean;
  /** fields with Cat A / showIf / severityOptional ALREADY resolved by the calling side. */
  fields: SectionProgressField[];
  rows: RawProgressRow[];
  manualNa?: boolean;
  /** s156 Part B — per-field gates, so the builder can resolve showIf PER ROW for list sections.
   *  Omitted → no field is treated as hidden (previous behaviour). */
  gates?: FieldGates;
}

/**
 * Assemble the shared math's input: resolve per-row visibility, the completion mode, the shaded set and
 * the header field — all from config both sides now share. The caller supplies rows + resolved fields;
 * every decision below is made in exactly one place.
 */
export function buildSectionProgressInput(args: BuildProgressInputArgs): SectionProgressInput {
  const { sectionSlug, layoutType, fields, rows, manualNa } = args;
  const declaresEmptyStarter = args.declaresEmptyStarter;

  // D-156: completion measures exactly the rows the planner can SEE. A card collapses to one always-
  // visible synthetic row; a list row is visible unless it's a legacy blank artifact or soft-hidden.
  const gates = args.gates;
  // s234 (D-233.72, D-234.43): a child's Beneficiaries row is the child on Children, shown on Beneficiaries — it is
  // measured on Children, never here, so it neither completes Beneficiaries nor holds it open for answers a child's
  // row does not ask (their contact details). The lens reads this same input, so it drops out there too.
  const measured = sectionSlug === BENEFICIARIES_SLUG ? rows.filter((r) => !isChildBeneficiaryEntries(r.entries)) : rows;
  const progressRows = measured.map((r) => {
    // s156 Part B — resolve each gated field's showIf against THIS ROW's own cells. showIf targets a
    // sibling by fieldId, so the entry map is keyed by fieldId. Card sections keep resolving showIf in
    // the client adapter (single-instance, and it feeds `hiddenByShowIf` on the FIELD, not the row).
    let hiddenFieldKeys: string[] | undefined;
    if (gates && layoutType !== "card") {
      // s223 (D-223.18): the resolution moved to `shared/lib/row-gates` so the document read path asks the
      // SAME question this does — a value a row does not ask must not reach a court form either.
      const hiddenList = hiddenFieldKeysOnRow(
        Object.entries(gates).flatMap(([fieldKey, g]) => (g ? [{ fieldKey, fieldId: g.fieldId, conditions: g.conditions }] : [])),
        r.entries,
      );
      if (hiddenList.length) hiddenFieldKeys = hiddenList;
    }
    return {
      rowId: r.rowId,
      entries: r.entries.map((e): SectionProgressEntry => ({
        fieldKey: e.fieldKey,
        value: e.value,
        isSeeded: e.isSeeded,
        declined: e.declined === true,
      })),
      visible:
        layoutType === "card" ||
        (!isLegacyBlankRow(r.entries, declaresEmptyStarter) && !isRowHidden(r.rowId, r.entries)),
      hiddenFieldKeys,
    };
  });

  const headerCfg = EXPANDABLE_LAYOUT_CONFIG[sectionSlug]?.headerField;
  const headerFieldKey = headerCfg && headerCfg.drivesCompletion !== false ? headerCfg.fieldKey : undefined;
  // 🔴 s185 (D-185.1): SHADED MODE IS RETIRED. The completion-modes map no longer feeds
  // completion — the strict D-156 rule measures every list section, including the 16 that used to
  // declare `"shaded"`. The map survives only as inventory metadata (`dump-section-inventory.ts`)
  // and as the renderer's top-zone shading, which is PRESENTATION and unaffected.

  return { sectionSlug, layoutType, fields, rows: progressRows, headerFieldKey, manualNa };
}
